/**
 * nodeFactory.ts
 * 
 * A lightweight factory that allows node constructors to re-instantiate
 * raw child objects without creating circular import dependencies.
 * 
 * Usage:
 *  - serializeNode.ts calls `setNodeFactory(deserializeNode)` on import
 *  - Node constructors call `reinstantiate(rawObj)` to get a full class instance
 */

import type { StrategyNode } from '../types/BaseNode';

type DeserializeFn = (data: any) => StrategyNode;

let _factory: DeserializeFn | null = null;

/**
 * Register the deserializeNode function.
 * Called once from serializeNode.ts at module load time.
 */
export function setNodeFactory(fn: DeserializeFn): void {
    _factory = fn;
}

/**
 * Re-instantiate a raw object as a proper StrategyNode.
 * If the factory is not registered or the input already has methods, return as-is.
 */
export function reinstantiate(obj: any): StrategyNode {
    if (!obj) return obj;
    // Already a full instance if it has the update method
    if (typeof obj.update === 'function') return obj as StrategyNode;
    // Use registered factory if available
    if (_factory) {
        try {
            return _factory(obj);
        } catch (e) {
            console.warn('[nodeFactory] Could not re-instantiate node:', obj, e);
        }
    }
    return obj as StrategyNode;
}
